import { VFXEffectConfig, VFXEffectRegistry } from './VFXEffectRegistry';
import { gameLogger } from '../../utils/GameLogger';
import { ServiceRegistry } from '../core/ServiceRegistry';
import { EnhancedParticleManager } from './EnhancedParticleManager';
import type { IShape } from '../rendering/IRenderer';

import { ScreenEffectManager } from './managers/ScreenEffectManager';
import { FloatingTextManager } from './managers/FloatingTextManager';
import { AnimatedTextManager } from './text/AnimatedTextManager';
import { scheduleEffectExpiry } from './players/effectLifetime';

import { OrbiterSystem } from './players/OrbiterSystem';
import { FollowerSystem } from './players/FollowerSystem';
import { BeamLinkSystem } from './players/BeamLinkSystem';
import { TrailSystem } from './players/TrailSystem';
import { EntityOverrideSystem } from './players/EntityOverrideSystem';
import { ShapeSpawner } from './players/ShapeSpawner';
import { PersistentEffectTracker } from './players/PersistentEffectTracker';
import { DebugFCT } from '../debug/DebugFCT';
import { applyDefScreenFeel } from './screenFeelScope';
import { applyAreaRadius } from './area/effectFootprint';

/**
 * Prefix of world-anchored summon/zone persistent instanceIds — matches the
 * ids SummonBehavior mints (`summon_${abilityId}_${Date.now()}`). Tracker
 * entries under this prefix are GROUND-anchored (zone-builders spec §4), not
 * entity-attached, so the K24 map-switch sweep stops them while preserving
 * entity-riding persistents (status auras, shields).
 */
export const ZONE_INSTANCE_PREFIX = 'summon_';

export class VFXEffectPlayer {
  private scene: unknown;
  public particleManager: EnhancedParticleManager;
  public screenEffects: ScreenEffectManager;
  public floatingText: FloatingTextManager;
  public animatedText: AnimatedTextManager;

  // Subsystems
  private orbiterSystem: OrbiterSystem;
  private followerSystem: FollowerSystem;
  private beamLinkSystem: BeamLinkSystem;
  private entityOverrideSystem: EntityOverrideSystem;
  private shapeSpawner: ShapeSpawner;
  private persistentTracker: PersistentEffectTracker;

  constructor(scene: unknown) {
    this.scene = scene;
    this.particleManager = EnhancedParticleManager.getInstance(scene);
    this.screenEffects = new ScreenEffectManager(scene);
    this.floatingText = new FloatingTextManager(scene);
    this.animatedText = new AnimatedTextManager(scene);

    this.orbiterSystem = new OrbiterSystem();
    this.followerSystem = new FollowerSystem();
    this.beamLinkSystem = new BeamLinkSystem();
    this.entityOverrideSystem = new EntityOverrideSystem();
    this.shapeSpawner = new ShapeSpawner(scene);
    this.persistentTracker = new PersistentEffectTracker();
    // Trail ribbons + dash ghosts (module singleton — position owners feed
    // it directly; we own its scene + per-frame pump).
    TrailSystem.setScene(scene);
  }

  public setScene(scene: unknown): void {
    this.scene = scene;
    this.particleManager = EnhancedParticleManager.getInstance(scene);
    this.screenEffects.setScene(scene);
    this.floatingText.setScene(scene);
    this.animatedText.setScene(scene);
    this.shapeSpawner.setScene(scene);
    this.orbiterSystem.clear();
    this.followerSystem.clear();
    this.beamLinkSystem.clear();
    this.entityOverrideSystem.clear();
    this.persistentTracker.clear();
    TrailSystem.setScene(scene);
  }

  /**
   * K24 (2026-07-17) in-place map-switch sweep: release every UN-tracked live
   * shape (in-flight bursts + leaked repeat:-1 primitives like ice_wall).
   * ENTITY-attached PersistentEffectTracker entries (status auras, mana shield,
   * orbit charge stacks, combat-feedback persistents) are PRESERVED: the
   * entities survive an in-place server map switch, and their visuals are
   * event-driven with no re-derive path — sweeping them would eat live auras.
   * A genuinely leaked tracker entry is already backstopped by sweepStale's
   * 60s ceiling in update(). Follower/orbiter bookkeeping for released shapes
   * self-prunes on the next update() tick (`!obj.active` check).
   *
   * WORLD-anchored tracker entries are the exception (zone-builders spec §4):
   * summon zones/traps/totems (instanceIds minted by SummonBehavior as
   * `summon_<abilityId>_<ts>`, ZONE_INSTANCE_PREFIX) are placed on the OLD
   * map's ground and their `vfx:summon_expired` may fire against a map that
   * no longer exists — stop them outright here.
   */
  public sweepTransientShapes(): number {
    let zoneShapes = 0;
    for (const key of this.persistentTracker.getKeysMatching(ZONE_INSTANCE_PREFIX)) {
      if (!key.startsWith(ZONE_INSTANCE_PREFIX)) continue;
      zoneShapes += this.persistentTracker.get(key)?.length ?? 0;
      this.stopPersistentEffect(key);
    }
    const preserved = new Set<IShape>();
    for (const key of this.persistentTracker.getKeysMatching('')) {
      for (const obj of this.persistentTracker.get(key) ?? []) preserved.add(obj);
    }
    return this.shapeSpawner.sweepLive(preserved) + zoneShapes;
  }

  /**
   * Play an effect tracked by a custom instance ID (orbits, auras, statuses). Re-playing an id
   * stops what it replaces: a refreshed status used to orphan its old aura (plan 15 §8).
   */
  public playPersistent(
    instanceId: string,
    effectId: string,
    x: number, y: number,
    overrides?: { color?: number; scale?: number; duration?: number; targetId?: string; [key: string]: any }
  ): IShape[] {
    if (this.persistentTracker.get(instanceId)) this.stopPersistentEffect(instanceId);
    const spawned = this.playById(effectId, x, y, overrides);
    if (spawned.length > 0) {
      this.persistentTracker.track(instanceId, spawned);
      DebugFCT.info('vfx', `Persist: ${effectId}`, x, y);
    }
    return spawned;
  }

  /**
   * Remove a specific primitive from a persistent effect instance by index.
   */
  public destroyPersistentPrimitive(instanceId: string, index: number): void {
    this.persistentTracker.removePrimitive(instanceId, index, this.scene, (obj) => {
      this.destroyObject(obj);
    });
  }

  /**
   * Stop and cleanup a persistent effect instance early.
   */
  public stopPersistentEffect(instanceId: string): void {
    DebugFCT.infoPlayer('vfx', `Stop: ${instanceId}`);
    this.persistentTracker.stop(instanceId, (obj) => {
      if (obj && obj.active) this.destroyObject(obj);
    });
    this.entityOverrideSystem.revertMatching(instanceId);
  }

  public play(
    config: VFXEffectConfig,
    x: number,
    y: number,
    overrides?: { color?: number; scale?: number; duration?: number; targetId?: string; [key: string]: any }
  ): IShape[] {
    if (!this.scene || !config) {
      DebugFCT.fail('vfx', `No scene for ${config?.id || '?'}`, x, y);
      return [];
    }

    DebugFCT.success('vfx', config.id, x, y);
    [config, overrides] = applyAreaRadius(config, overrides); // plan 29 F6: drawn at the gameplay radius
    const spawnedObjects: IShape[] = [];
    const followTargetId = overrides?.targetId || config.followTarget;
    // Where on the target a follower sits (e.g. a projectile's contact point), as an offset from the
    // target's centre; without it a follower snaps onto the centre on its next frame.
    const followOffset = (overrides as { followOffset?: { x: number; y: number } } | undefined)?.followOffset;
    const followDx = followOffset?.x ?? 0, followDy = followOffset?.y ?? 0;

    // Multi-tone defs OWN their palette (U-recent-delta-4): a play-time color
    // override exists to tint single-tone generated effects (defensive_aura,
    // school bursts) — flat-tinting a def authored with distinct fills
    // (shadow_weapon_aura's alternating blades + white glint) erased its
    // entire palette to one silhouette. ≥2 distinct fill colors → authored
    // intent → the override is dropped for shapes (scale/duration still apply).
    if (overrides?.color !== undefined) {
      const fills = new Set<number>();
      for (const p of config.primitives) {
        if (p.type !== 'particles' && p.type !== 'entity_override' && p.color !== undefined) fills.add(p.color);
      }
      if (fills.size > 1) {
        overrides = { ...overrides, color: undefined };
      }
    }

    for (const prim of config.primitives) {
      // Handle particles
      if (prim.type === 'particles') {
        if (prim.particleEffectId) {
          this.particleManager.spawnBurst(prim.particleEffectId, x, y, prim.particleCount || 10);
        }
        continue;
      }

      // Handle entity overrides
      if (prim.type === 'entity_override') {
        this.entityOverrideSystem.apply(this.scene, prim, overrides, config);
        continue;
      }

      // Ribbon primitives route to TrailSystem (trails spec §5) — never
      // through ShapeSpawner: one pooled graphics object per ribbon, redrawn
      // from the feed history. Requires a `trailKey` spawn override (the fed
      // projectile/entity id); a ribbon played without one has no feeder and
      // is skipped (e.g. a legacy dash one-shot reusing a `${id}_trail` def).
      if (prim.type === 'ribbon') {
        const trailKey = (overrides as { trailKey?: string } | undefined)?.trailKey;
        if (trailKey) {
          TrailSystem.attach(String(trailKey), prim, {
            abilityId: (overrides as { abilityId?: string } | undefined)?.abilityId,
          });
        } else {
          gameLogger.debug('vfx', `[VFXEffectPlayer] ribbon in ${config.id} without trailKey — skipped`);
        }
        continue;
      }

      // Two-endpoint links (beam-builders spec §4): the shape is stretched +
      // rotated caster→target every frame by BeamLinkSystem. Linked
      // primitives ignore flipX — the link owns orientation (a left-facing
      // cast would double-mirror otherwise); facing only signs the
      // targetless-fallback reach. Played with no caster id at all (direct
      // QA playback), a linked primitive degrades to the plain spawn path
      // below (fixed-length facing beam — today's behavior).
      if (prim.link) {
        const casterAnchorId = (overrides as { casterId?: string } | undefined)?.casterId || followTargetId;
        if (casterAnchorId) {
          const facingSign = overrides?.flipX === true ? -1 : 1;
          const linkOverrides = { ...overrides, flipX: undefined };
          const obj = this.shapeSpawner.spawnWithTween(prim, x, y, linkOverrides, (o) => {
            this.destroyObject(o);
          });
          if (obj) {
            spawnedObjects.push(obj);
            const authoredLength = prim.beamLength ?? 100;
            this.beamLinkSystem.add(obj, {
              casterId: casterAnchorId,
              targetId: (overrides as { linkTargetId?: string } | undefined)?.linkTargetId,
              abilityId: (overrides as { abilityId?: string } | undefined)?.abilityId ?? null,
              authoredLength,
              maxLengthPx: prim.link.maxLengthPx,
              fallbackDx: facingSign * authoredLength,
              mote: prim.link.mote,
            });
          }
          continue;
        }
      }

      if (prim.scatter) {
        const scattered = this.shapeSpawner.spawnScattered(prim, x, y, overrides, (obj) => {
          this.destroyObject(obj);
        });
        for (const obj of scattered) {
          spawnedObjects.push(obj);
          if (followTargetId && !prim.orbit) {
            this.followerSystem.add(obj, followTargetId, obj.x - x + followDx, obj.y - y + followDy);
          }
        }
      } else {
        const obj = this.shapeSpawner.spawnWithTween(prim, x, y, overrides, (o) => {
          this.destroyObject(o);
        });
        if (obj) {
          spawnedObjects.push(obj);

          if (prim.orbit) {
            this.orbiterSystem.add(obj, prim.orbit, x, y, followTargetId);
          } else if (followTargetId) {
            this.followerSystem.add(obj, followTargetId, obj.x - x + followDx, obj.y - y + followDy);
          }
        }
      }
    }

    // Screen-wide feel — skipped for remote-cast replays (screenFeelScope).
    applyDefScreenFeel(config, overrides, this.screenEffects);

    if (config.floatingText) {
      let text = config.floatingText.text;
      if ((overrides as any)?.amount !== undefined) text = text.replace('{amount}', (overrides as any).amount.toString());
      if ((overrides as any)?.abilityName !== undefined) text = text.replace('{abilityName}', (overrides as any).abilityName);
      if ((overrides as any)?.itemName !== undefined) text = text.replace('{itemName}', (overrides as any).itemName);
      if ((overrides as any)?.newLevel !== undefined) text = text.replace('{newLevel}', (overrides as any).newLevel.toString());

      this.floatingText.showFloatingText(text, x, y, {
        text,
        color: config.floatingText.color || '#ffffff',
        fontSize: config.floatingText.fontSize || '16px',
        duration: config.floatingText.duration || 1000,
        offsetY: config.floatingText.offsetY || 20,
        style: config.floatingText.style || 'info',
        bounce: config.floatingText.bounce || false,
        fade: config.floatingText.fade || true
      });
    }

    if (config.animatedText) {
      let text = config.animatedText.text;
      if ((overrides as any)?.amount !== undefined) text = text.replace('{amount}', (overrides as any).amount.toString());
      if ((overrides as any)?.abilityName !== undefined) text = text.replace('{abilityName}', (overrides as any).abilityName);
      if ((overrides as any)?.itemName !== undefined) text = text.replace('{itemName}', (overrides as any).itemName);
      if ((overrides as any)?.newLevel !== undefined) text = text.replace('{newLevel}', (overrides as any).newLevel.toString());
      this.animatedText.show({ ...config.animatedText, text, x, y });
    }

    const totalDuration = overrides?.duration || config.duration;
    if (totalDuration) {
      scheduleEffectExpiry(spawnedObjects, totalDuration,
        obj => this.shapeSpawner.fadeOut(obj, 200, o => this.destroyObject(o)));
    }

    return spawnedObjects;
  }

  public playById(
    effectId: string,
    x: number,
    y: number,
    overrides?: { color?: number; scale?: number; duration?: number; targetId?: string; [key: string]: any }
  ): IShape[] {
    const config = VFXEffectRegistry.get(effectId);
    if (!config) {
      gameLogger.debug('vfx', `[VFXEffectPlayer] Unknown effect: ${effectId}`);
      DebugFCT.fail('vfx', `Unknown: ${effectId}`, x, y);
      return [];
    }
    return this.play(config, x, y, overrides);
  }

  public update(_time: number, delta: number): void {
    const dt = delta / 1000;
    const resolveTarget = this.resolveTarget.bind(this);

    this.orbiterSystem.update(dt, resolveTarget);
    this.followerSystem.update(resolveTarget);
    this.beamLinkSystem.update(resolveTarget);
    TrailSystem.update(Date.now());
    this.persistentTracker.sweepStale((obj) => this.destroyObject(obj));
  }

  /** Read-only live beam count for the test-gated probe surface. */
  public getActiveBeamCount(): number {
    return this.beamLinkSystem.getActiveCount();
  }

  /**
   * Resolve a follower/orbiter target id to an entity with `.position`.
   * Order: local player (id match or blank) → remote player → mob (position
   * shim around the sprite). Returns null for unresolvable ids — a follower
   * then HOLDS its spawn position, which is honest; the old code fell back
   * to the local player for EVERY id (getEntity never existed on
   * EntitySystem), so any aura on a remote player or mob would have glued
   * itself to the local player instead.
   */
  private resolveTarget(id: string): any {
    const registry = ServiceRegistry.getInstance();
    if (!registry.has('EntitySystem')) return null;
    const entitySystem = registry.get('EntitySystem') as any;
    const local = entitySystem?.getPlayerManager?.()?.getPlayer?.();
    const localSessionId = this.getLocalSessionId();
    if (!id || (local && (local.id === id || (localSessionId && id === localSessionId)))) return local ?? null;

    const remote = entitySystem?.getRemotePlayerManager?.()?.getOtherPlayers?.()?.get?.(id);
    if (remote?.position) return remote;

    // Mobs — MobManager keeps sprite-centre x/y; shim it into the
    // `.position` shape FollowerSystem/OrbiterSystem read. Live getter, not
    // a snapshot: FollowerSystem caches the resolved target across frames.
    const mob = registry.get('MobSystem', { optional: true })?.getManager?.()?.getMob?.(id);
    if (mob && typeof mob.x === 'number') {
      return { get position() { return { x: mob.x, y: mob.y }; } };
    }
    return null;
  }

  /** The server identifies the local player by the Colyseus session ID,
   *  but the engine player object may still carry a dev/character id. When
   *  a follow target is the current session, resolve to the local player so
   *  status auras and anchored VFX travel with the player. */
  private getLocalSessionId(): string | null {
    try {
      const nm = ServiceRegistry.getInstance().get('NetworkManager', { optional: true })
        || (typeof globalThis !== 'undefined' ? (globalThis as any).nm : null);
      return nm?.getRoom?.()?.sessionId ?? null;
    } catch {
      return null;
    }
  }

  /**
   * Coarse, read-only summary of effects currently alive in this player's
   * subsystems — used by the render-truth hook (Lane 5 remote-AoE-VFX parity)
   * to detect an effect's PRESENCE + position on a remote client. NOT a deep
   * shape introspection: we report one row per live shape with its world
   * position, an instance id, and a category-derived `kind`/`effectId`.
   *
   * Each subsystem keeps its tracked shapes in a private Map; we read those
   * maps without mutating them. Every access is guarded so a not-yet-ready
   * subsystem contributes nothing rather than throwing into the test surface.
   */
  public getActiveEffectSummary(): Array<{
    kind: string;
    instanceId: string;
    x: number;
    y: number;
    effectId?: string;
  }> {
    const rows: Array<{ kind: string; instanceId: string; x: number; y: number; effectId?: string }> = [];

    const liveShape = (obj: any): obj is { x: number; y: number; active: boolean } =>
      !!obj && obj.active === true && typeof obj.x === 'number' && typeof obj.y === 'number';

    // Shapes already surfaced by a tracker below — used to dedup the transient
    // sweep (a tracked shape is also in ShapeSpawner's live set, but it must
    // be reported once, under its tracker kind, not twice).
    const trackedObjs = new Set<any>();

    // PersistentEffectTracker — Map<instanceId, { objects: IShape[] }>.
    try {
      const effects: Map<string, { objects: any[] }> | undefined = (this.persistentTracker as any)?.effects;
      effects?.forEach((entry, instanceId) => {
        let i = 0;
        for (const obj of entry?.objects ?? []) {
          if (liveShape(obj)) {
            rows.push({ kind: 'persistent', instanceId: `${instanceId}#${i}`, x: obj.x, y: obj.y, effectId: instanceId });
            trackedObjs.add(obj);
          }
          i++;
        }
      });
    } catch { /* subsystem not ready — contribute nothing */ }

    // OrbiterSystem — Map<IShape, OrbiterData>. Key IS the shape (has x/y).
    try {
      const orbiters: Map<any, any> | undefined = (this.orbiterSystem as any)?.orbiters;
      let i = 0;
      orbiters?.forEach((data, obj) => {
        if (liveShape(obj)) {
          rows.push({ kind: 'orbiter', instanceId: `orbiter#${i}`, x: obj.x, y: obj.y, effectId: data?.followTarget });
          trackedObjs.add(obj);
        }
        i++;
      });
    } catch { /* subsystem not ready */ }

    // FollowerSystem — Map<IShape, FollowerData>. Key IS the shape.
    try {
      const followers: Map<any, any> | undefined = (this.followerSystem as any)?.followers;
      let i = 0;
      followers?.forEach((data, obj) => {
        if (liveShape(obj)) {
          rows.push({ kind: 'follower', instanceId: `follower#${i}`, x: obj.x, y: obj.y, effectId: data?.targetId });
          trackedObjs.add(obj);
        }
        i++;
      });
    } catch { /* subsystem not ready */ }

    // Fire-and-forget transients (scatter snow, falling particles) — owned by
    // no tracker, so they were invisible to render-truth before (only a coarse
    // budget count). ShapeSpawner now exposes its live set; report each by
    // position, skipping any already counted above.
    try {
      const live: any[] = (this.shapeSpawner as any)?.getLiveShapes?.() ?? [];
      let i = 0;
      for (const obj of live) {
        if (!trackedObjs.has(obj) && liveShape(obj)) {
          rows.push({ kind: 'transient', instanceId: `transient#${i}`, x: obj.x, y: obj.y, effectId: 'shape_spawner' });
        }
        i++;
      }
    } catch { /* spawner not ready — contribute nothing */ }

    // Last-resort coarse presence: if nothing concrete surfaced but the budget
    // says shapes are live (e.g. getLiveShapes unavailable), at least flag it.
    if (rows.length === 0) {
      try {
        const stats: any = (this.shapeSpawner as any)?.getActiveStats?.();
        const liveCount = typeof stats?.activeGraphics === 'number'
          ? stats.activeGraphics
          : (typeof stats?.activeShapes === 'number' ? stats.activeShapes : 0);
        if (liveCount > 0) {
          rows.push({ kind: 'shape_budget', instanceId: `shape_budget#live=${liveCount}`, x: 0, y: 0, effectId: 'shape_spawner' });
        }
      } catch { /* budget manager not ready */ }
    }

    return rows;
  }

  private destroyObject(obj: IShape): void {
    this.orbiterSystem.remove(obj);
    this.followerSystem.remove(obj);
    this.beamLinkSystem.remove(obj);
    this.shapeSpawner.releaseShape(obj);
  }
}
